/* eslint-disable no-useless-concat */
import { ReactComponent as NoDataChartIcon } from "assets/chart-no-data.svg"
import { ReactComponent as ErrorChartIcon } from "assets/chart-error.svg"
import { useI18n } from "providers/I18n"
import React, { useCallback, useEffect, useState } from "react"
import { Loading } from "components/Loading"
import { Box, Grid } from "@material-ui/core"
import { RootLayout } from "./RootLayout"
import BarChart from "./../Charts/BarStackSection/BarChart"
import { LineChartLayout } from "./../Charts/LineChartSection/LineChartLayout"
import ParentSize from "@visx/responsive/lib/components/ParentSize"
import { GcfTable } from "./DetailsViewTables/GcfTable"
import {
  ExpandBarStackDialog,
  ExpandBarStackDialogRef,
} from "./ExpandBarStackDialog"
import {
  ExpandLineChartDialog,
  ExpandLineChartDialogRef,
} from "./ExpandLineChartDialog"
import FiltersCharts from "./FiltersCharts"
import { useWindowDimensions } from "../../hooks/useWindowDimensions"
import { EmptyReponseView } from "../Charts/EmptyReponseView"
import { TitleWithFilters } from "./DetailsLayout/TitleWithFilters"
import { ChartTopLayout } from "./DetailsLayout/ChartTopLayout"
import { DeltaCheckbox } from "./DetailsLayout/DeltaCheckbox"
import { LineChartTopButtonsLayout } from "./DetailsLayout/LineChartLayout/LineChartTopButtonsLayout"
import { ExpandButton } from "./DetailsLayout/ExpandLineChartDialogLayout/ExpandButton"
import { ChartRootLayout } from "./DetailsLayout/ChartRootLayout"
import { ChartSection } from "./DetailsLayout/ChartSection"
import { DetailsTableRootLayout } from "./DetailsLayout/DetailsTableRootLayout"
import { useQuery } from "react-query"
import { fetchDetailsGCF } from "../../api"
import { getTotalEntityName } from "../../utils"

const defaultEntityItems = [[], [], [], [], []]

const defaultSetting = (isEnabled: boolean) => {
  return defaultEntityItems.map(() => isEnabled)
}

const firstBarInfos = {
  revenue: ["targetRevenue", "ytdRevenue", "previousScenarioRevenueValue"],
  oibiaPercent: ["targetOibia", "ytdOibia", "previousScenarioOIPercentage"],
}

const oibiaVsKeys = [
  "targetVSYearToDateOiDelta",
  "yearToDateVSPreviousOiDelta",
  "previousVSActualOiDelta",
]
const netVsKeys = [
  "targetVSYearToDateRevenueDelta",
  "yearToDateVSPreviousRevenueDelta",
  "previousVSActualRevenueDelta",
]

type GcfViewProps = {
  search: string
}
export function GcfView(props: GcfViewProps) {
  const { t } = useI18n()
  const windowDimensions = useWindowDimensions()

  const { search } = props
  const updated = new URLSearchParams(search)
  const year = updated.get("year") || ""
  const scenario = updated.get("scenario") || ""

  const expandBarStackDialogRef = React.useRef<ExpandBarStackDialogRef>()
  const expandLineChartDialogRef = React.useRef<ExpandLineChartDialogRef>()

  const [availableEntities, setAvailableEntities] =
    React.useState<string[][]>(defaultEntityItems)

  const [legendItems, setLegendItems] = React.useState(defaultEntityItems)

  const [availableTables, setAvailableTables] = React.useState<
    { key: string; value: any }[]
  >([])

  const [showOI, setShowOI] = useState(() => defaultSetting(false))

  const [openSettings, setOpenSettings] = useState(() => defaultSetting(false))
  const [revenueChecked, setRevenueChecked] = useState(() =>
    defaultSetting(true),
  )
  const [checkAll, setCheckAll] = useState(() => defaultSetting(true))

  const [hasNoData, setHasNoData] = useState(false)
  const [hasError, setHasError] = useState(false)

  const { data, isLoading } = useQuery(["gcfData", search], fetchDetailsGCF, {
    onSuccess: () => {
      updated.set("year", year)
      updated.set("scenario", scenario)
    },
    onError: () => setHasError(true),
    enabled: !!search,
  })

  const handleChangeAvailableEntities = useCallback(
    (idx, entity) => {
      let array = [...availableEntities] as any
      let arrayAll = [...checkAll]
      const isEntityEnabled = array[idx].indexOf(entity) !== -1
      const entityIdx = array[idx].indexOf(entity)
      if (isEntityEnabled) {
        array[idx].splice(entityIdx, 1)
      } else {
        array[idx].push(entity)
      }
      setAvailableEntities(array)
      if (legendItems[idx].length !== availableEntities[idx].length) {
        arrayAll[idx] = false
        setCheckAll(arrayAll)
      } else {
        arrayAll[idx] = true
        setCheckAll(arrayAll)
      }
    },
    [availableEntities, checkAll, legendItems],
  )

  const handleCheckAllEntities = useCallback(
    (i, legendItems) => {
      let array = [...checkAll]
      let availEntities = [...availableEntities]

      array[i] = !checkAll[i]
      if (!checkAll[i]) {
        availEntities[i] = [...legendItems]
      } else {
        availEntities[i] = []
      }
      setAvailableEntities(availEntities)
      setCheckAll(array)
    },
    [availableEntities, checkAll],
  )

  const handleRevenueCheck = useCallback(
    (i) => {
      let array = [...revenueChecked]
      array[i] = !revenueChecked[i]
      setRevenueChecked(array)
    },
    [revenueChecked, setRevenueChecked],
  )
  const handleOiChange = useCallback(
    (i) => {
      let array = [...showOI]
      array[i] = !showOI[i]
      setShowOI(array)
    },
    [showOI],
  )

  const handleOpenSettings = useCallback(
    (i) => {
      let array = [...openSettings]
      array[i] = !openSettings[i]
      setOpenSettings(array)
    },
    [openSettings],
  )

  const isBUDScenario = scenario === "BUD"

  const curYear = new Date().getFullYear()

  const customTitle1 = `${curYear} ${t("revenue.target")}`

  const customTitle2 = `${t("revenue.current")} (${year} ${scenario}) ${t(
    "revenue.vs",
  )} ${t("revenue.target")}`

  const customTitle3 = `${t("revenue.current")} (${year} ${scenario})`

  const customTitle4 = `${t("revenue.current")} (${year} ${scenario}) ${t(
    "revenue.vs",
  )} ${t(
    "revenue.previous",
  )} (${data?.yearForPreviousScenario} ${data?.previousScenario})`

  const customTitle5 = `${t(
    "revenue.previous",
  )} (${data?.yearForPreviousScenario} ${data?.previousScenario})`

  const customTitle6 = `${t("revenue.current")} (${year} ${
    isBUDScenario ? t("revenue.title.bud") : scenario
  }) ${t("revenue.vs")} ${t("revenue.prior")} (${
    isBUDScenario ? year : Number(year) - 1
  } ${isBUDScenario ? t("revenue.fp") : t("revenue.act")})`

  const customTitles = [
    customTitle1,
    customTitle2,
    customTitle3,
    customTitle4,
    customTitle5,
    customTitle6,
  ]

  useEffect(() => {
    if (data) {
      const hasData = Object.keys(data?.data).filter(
        (key) => !!data?.data[key].length,
      )
      setHasError(false)
      if (!hasData.length) {
        setLegendItems(defaultEntityItems)
        setAvailableTables([])
        setAvailableEntities(defaultEntityItems)
        setHasNoData(true)
        return
      }
      setHasNoData(false)
      setLegendItems(
        Object.keys(data?.data)
          .map((key) => data?.data[key].map((x: any) => x?.entityName))
          .filter((x) => !!x.length),
      )

      setAvailableEntities(
        Object.keys(data?.data)
          .map((key) => data?.data[key].map((x: any) => x?.entityName))
          .filter((x) => !!x.length),
      )

      setAvailableTables(
        Object.keys(data?.data)
          .map((x) =>
            !!data?.data[x].length
              ? {
                  key: x,
                  value: data?.data[x],
                }
              : { key: x, value: [] },
          )
          .filter((x) => !!x.value.length),
      )
    }
  }, [data])

  if (isLoading) {
    return <Loading />
  }

  if (hasNoData || hasError) {
    return (
      <Box
        height={windowDimensions.height / 1.7}
        display="flex"
        justifyContent="center"
      >
        <EmptyReponseView
          icon={hasNoData ? <NoDataChartIcon /> : <ErrorChartIcon />}
          title={
            hasNoData ? t("revenue.noData.title") : t("revenue.error.title")
          }
          subTitle={
            hasNoData
              ? t("revenue.noData.subTitle")
              : t("revenue.error.subTitle")
          }
        />
      </Box>
    )
  }

  return (
    <>
      {availableTables.map((item, itemIndex) => (
        <div key={itemIndex}>
          <RootLayout>
            <Box>
              <TitleWithFilters title={item.key}>
                <FiltersCharts
                  itemKey={item.key}
                  legendItems={legendItems[itemIndex]}
                  itemIndex={itemIndex}
                  availableEntities={availableEntities}
                  handleChangeAvailableEntities={handleChangeAvailableEntities}
                  isOpen={openSettings[itemIndex]}
                  handleOpen={() => handleOpenSettings(itemIndex)}
                  handleOpenSettings={handleOpenSettings}
                  handleCheckAllEntities={handleCheckAllEntities}
                  checked={checkAll[itemIndex]}
                />
              </TitleWithFilters>

              <Grid
                container
                justify="space-between"
                direction={windowDimensions.isSmallScreen ? "column" : "row"}
              >
                <ChartSection>
                  <Box display="flex" flexDirection="column">
                    <ChartTopLayout
                      title={`${t("revenue.usd")} / ${t("revenue.oi")}`}
                    >
                      <ExpandButton
                        onClick={() =>
                          expandBarStackDialogRef.current?.open(
                            item.key,
                            itemIndex,
                          )
                        }
                      />
                    </ChartTopLayout>
                    <Box style={{ height: 38 }} />
                  </Box>
                  <ChartRootLayout>
                    <ParentSize debounceTime={10}>
                      {({ width: visWidth }) => (
                        <BarChart
                          tableData={data}
                          width={visWidth}
                          height={500}
                          search={search}
                          entity={item.key}
                          firstBarInfos={firstBarInfos}
                          dataType="gcf"
                          availableEntities={availableEntities}
                          entityIndex={itemIndex}
                          showSecondBar={false}
                        />
                      )}
                    </ParentSize>
                  </ChartRootLayout>
                </ChartSection>
                {!windowDimensions.isSmallScreen && <Box padding="0px 32px" />}
                <ChartSection>
                  <Box display="flex" flexDirection="column">
                    <ChartTopLayout title="Δ REVENUE (%) / Δ OI (%)">
                      <ExpandButton
                        onClick={() =>
                          expandLineChartDialogRef.current?.open(
                            item,
                            itemIndex,
                            getTotalEntityName(item.key, data?.total),
                          )
                        }
                      />
                    </ChartTopLayout>
                    <LineChartTopButtonsLayout>
                      <DeltaCheckbox
                        title={
                          `${getTotalEntityName(item.key, data?.total)} ${t(
                            "revenue.delta.revenue",
                          )}` + "(%)"
                        }
                        checked={revenueChecked[itemIndex]}
                        onChange={() => handleRevenueCheck(itemIndex)}
                      />

                      <DeltaCheckbox
                        title={`${getTotalEntityName(
                          item.key,
                          data?.total,
                        )} Δ OI (%)`}
                        checked={showOI[itemIndex]}
                        onChange={() => handleOiChange(itemIndex)}
                      />
                    </LineChartTopButtonsLayout>
                  </Box>
                  <ChartRootLayout>
                    <ParentSize debounceTime={10}>
                      {({ width: visWidth }) => (
                        <LineChartLayout
                          width={visWidth}
                          height={500}
                          item={
                            item as {
                              key: string
                              value: any
                            }
                          }
                          bottomTitles={[
                            customTitle2,
                            customTitle4,
                            customTitle6,
                          ]}
                          oibiaKeys={oibiaVsKeys}
                          netKeys={netVsKeys}
                          availableEntities={availableEntities}
                          dataType="gcf"
                          entityIndex={itemIndex}
                          showOI={showOI[itemIndex]}
                          showRevenue={revenueChecked[itemIndex]}
                        />
                      )}
                    </ParentSize>
                  </ChartRootLayout>
                </ChartSection>
              </Grid>
            </Box>
          </RootLayout>
          <Box padding="1px 0px" />
          <RootLayout isTable>
            <DetailsTableRootLayout>
              <GcfTable
                data={data}
                item={item}
                isLoading={isLoading}
                customTitles={customTitles}
              />
            </DetailsTableRootLayout>
          </RootLayout>
          {itemIndex < availableTables.length - 1 && <Box padding="12px 0px" />}
        </div>
      ))}
      <ExpandBarStackDialog
        data={data}
        firstBarInfos={firstBarInfos}
        dataType="gcf"
        search={search}
        availableEntities={availableEntities}
        ref={expandBarStackDialogRef}
        windowDimensions={windowDimensions}
        legendItems={legendItems}
        handleChangeAvailableEntities={handleChangeAvailableEntities}
        handleCheckAllEntities={handleCheckAllEntities}
        selectAllChecked={checkAll}
      />
      <ExpandLineChartDialog
        bottomTitles={[customTitle2, customTitle4, customTitle6]}
        oibiaKeys={oibiaVsKeys}
        netKeys={netVsKeys}
        dataType="gcf"
        availableEntities={availableEntities}
        ref={expandLineChartDialogRef}
        showOI={showOI}
        windowDimensions={windowDimensions}
        legendItems={legendItems}
        handleChangeAvailableEntities={handleChangeAvailableEntities}
        revenueChecked={revenueChecked}
        handleOiChange={handleOiChange}
        handleRevenueCheck={handleRevenueCheck}
        handleCheckAllEntities={handleCheckAllEntities}
        selectAllChecked={checkAll}
      />
    </>
  )
}
